import { faFlagSwallowtail } from '@fortawesome/pro-regular-svg-icons';
import { faFlagSwallowtail as faFlagSwallowtailSolid } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IIssueMachineHealthResponse } from '@kcftech/prometheustypescriptapiclient';
import { Button } from 'components/button/Button';
import { ButtonClickHandler } from 'components/button/ButtonProps';
import { StatelessPopup } from 'components/popup/stateless_popup/StatelessPopup';
import { usePopup } from 'custom_hooks/usePopup';
import { useUpdateIssue } from 'pages/Desk/issues/issue_details/useUpdateIssue';
import { memo, useMemo } from 'react';
import styled, { css } from 'styled-components';
import { tokens } from 'styles/tokens';
import { stopEvent } from 'utils/event';

const StyledButton = styled(Button)<{ $darkBackground: boolean; $highlighted?: boolean }>`
    width: 26px;
    height: 26px;

    border: 1px solid transparent;
    transition: none;
    border-radius: 5px;
    padding: 2px;
    display: flex;
    align-items: center;
    justify-content: center;

    ${({ $highlighted }) =>
        $highlighted &&
        css`
            background-color: ${tokens.background.subtle};
        `}

    &:disabled {
        background-color: transparent !important;
        border-color: transparent !important;
    }
    &:hover {
        background-color: ${({ $darkBackground }) =>
            $darkBackground ? tokens.surface.default : tokens.background.subtle};
        border-color: ${tokens.border.default};
    }
    &:active {
        transition: none;
        border-color: ${tokens.misc.favorite};
    }
`;

const Option = styled(Button)`
    padding: 1rem 2rem;
    border-radius: 0;
    text-align: left;
    font-size: 1.4rem;

    &:hover {
        background-color: ${tokens.background.subtle};
    }
`;

export type FlagButtonProps = Readonly<{
    issue: IIssueMachineHealthResponse;
    darkBackground?: boolean;
    className?: string;
}>;

function FlagButtonBase({ issue, darkBackground = false, className }: FlagButtonProps) {
    const {
        isOpen: isPopupOpen,
        open: showPopup,
        close: closePopup,
        popupRef,
        triggerRef,
    } = usePopup();

    const { updateIssueIsFlagged, isUpdatingIssueIsFlagged } = useUpdateIssue(issue);

    const buttonConfig = useMemo(() => {
        if (issue.IsFlagged) {
            return {
                icon: faFlagSwallowtailSolid,
                color: issue.ShouldReceiveNotifications
                    ? tokens.text.primary
                    : tokens.text.secondary,
                title: `Unflag issue${
                    issue.ShouldReceiveNotifications ? ' and end notifications' : ''
                }`,
            };
        }
        return {
            icon: faFlagSwallowtail,
            color: tokens.text.primary,
            title: 'Flag issue...',
        };
    }, [issue.IsFlagged, issue.ShouldReceiveNotifications]);

    const onClickFlag: ButtonClickHandler = (event) => {
        stopEvent(event);

        if (issue.IsFlagged) {
            updateIssueIsFlagged({ newValue: false, shouldReceiveNotifications: false });
        } else {
            showPopup();
        }
    };

    const toggleNotifications = (
        event: React.MouseEvent,
        shouldReceiveNotifications: boolean
    ) => {
        stopEvent(event);
        updateIssueIsFlagged({ newValue: true, shouldReceiveNotifications });
        closePopup();
    };

    return (
        <>
            <StyledButton
                ref={triggerRef}
                $darkBackground={darkBackground}
                $highlighted={isPopupOpen}
                title={buttonConfig.title}
                disabled={isUpdatingIssueIsFlagged}
                onClick={onClickFlag}
                className={className}
            >
                <FontAwesomeIcon color={buttonConfig.color} icon={buttonConfig.icon} />
            </StyledButton>
            {isPopupOpen && (
                <StatelessPopup
                    noPadding
                    anchor={triggerRef.current}
                    ref={popupRef}
                    onClose={closePopup}
                >
                    <div className="flex-column">
                        <Option onClick={(event) => toggleNotifications(event, false)}>
                            Flag
                        </Option>
                        <Option onClick={(event) => toggleNotifications(event, true)}>
                            Flag and be notified
                        </Option>
                    </div>
                </StatelessPopup>
            )}
        </>
    );
}

export const FlagButton = memo(FlagButtonBase);
